import type {
  CustomerServiceBreakdown,
  CustomerServiceMetric,
  CustomerServiceSnapshot,
  CustomerServiceTeam,
} from '~/hooks/apis/reporting/useCustomerServiceAnalytics';

import type { AreaStripTile } from './AreaMetricStrip';
import type { SplitStripGroup } from './LoyaltySplitStrip';
import { readAreaMetrics } from './areaMetricReading';

/**
 * Customer Service on Business Pulse (27-28 Oct 2026): Sales, Retail and Support as three cards, four headline tiles
 * each, grouped by each agent's current team. Repeat contacts, CSAT and the AI-handled share are left out for now:
 * CSAT is answered on about 1% of tickets, and the AI share has no reliable history yet.
 */
export const CS_TEAMS: ReadonlyArray<{ id: CustomerServiceTeam; label: string }> = [
  { id: 'sales', label: 'Sales' },
  { id: 'retail', label: 'Retail' },
  { id: 'support', label: 'Support' },
];

type Section = { id: string; title: string; metricIds: ReadonlyArray<string> };

export const CS_HEADLINE: Record<CustomerServiceTeam, ReadonlyArray<string>> = {
  sales: ['cs.sales.ttv', 'cs.sales.orders', 'cs.sales.conversion', 'cs.sales.abv'],
  // Store walk-ins are not interactions, so call conversion covers under half of Retail's orders; it sits under More
  // metrics, and Margin % takes its place.
  retail: ['cs.retail.ttv', 'cs.retail.orders', 'cs.retail.margin_pct', 'cs.retail.abv'],
  support: [
    'cs.support.contacts',
    'cs.support.service_level',
    'cs.support.tickets_solved',
    'cs.support.first_resolution_24h',
  ],
};

/** More metrics, in labelled sections under each card. */
export const CS_SECTIONS: Record<CustomerServiceTeam, ReadonlyArray<Section>> = {
  sales: [
    { id: 'sales-value', title: 'Value', metricIds: ['cs.sales.margin', 'cs.sales.margin_pct', 'cs.sales.aov'] },
    {
      id: 'sales-attach',
      title: 'Attachment',
      metricIds: ['cs.sales.flight_attach', 'cs.sales.insurance_attach', 'cs.sales.luxplus_attach'],
    },
    {
      id: 'sales-line',
      title: 'Sales line',
      metricIds: ['cs.sales.service_level', 'cs.sales.calls_answered', 'cs.sales.abandonment'],
    },
  ],
  retail: [
    { id: 'retail-value', title: 'Value', metricIds: ['cs.retail.margin', 'cs.retail.aov'] },
    {
      id: 'retail-attach',
      title: 'Attachment',
      metricIds: ['cs.retail.flight_attach', 'cs.retail.insurance_attach', 'cs.retail.luxplus_attach'],
    },
    { id: 'retail-calls', title: 'Calls', metricIds: ['cs.retail.conversion'] },
  ],
  support: [
    {
      id: 'support-volume',
      title: 'Volume',
      metricIds: [
        'cs.support.calls_answered',
        'cs.support.emails',
        'cs.support.chats',
        'cs.support.contacts_per_100_orders',
      ],
    },
    {
      id: 'support-service',
      title: 'Service',
      metricIds: ['cs.support.abandonment', 'cs.support.email_same_day', 'cs.support.aht'],
    },
  ],
};

/** The four tiles Home's Business Pulse row shows for Customer Service, one or two from each team. */
export const CS_HOME_TILES: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'cs.sales.ttv', label: 'Sales TTV (BR)' },
  { id: 'cs.retail.ttv', label: 'Retail TTV (BR)' },
  { id: 'cs.sales.conversion', label: 'Sales conversion' },
  { id: 'cs.support.service_level', label: 'Support call service level' },
];

/**
 * Every tile's label, so tiles keep their names while figures load or when they fail. The server's labels match these;
 * the team is named by the card, not the tile.
 */
export const CS_LABELS: Readonly<Record<string, string>> = Object.fromEntries(
  (['sales', 'retail'] as const)
    .flatMap((team) => [
      [`cs.${team}.ttv`, 'TTV (BR)'],
      [`cs.${team}.abv`, 'ABV'],
      [`cs.${team}.margin`, 'Gross margin'],
      [`cs.${team}.margin_pct`, 'Margin %'],
      [`cs.${team}.orders`, 'Orders'],
      [`cs.${team}.aov`, 'AOV'],
      [`cs.${team}.conversion`, 'Call conversion'],
      [`cs.${team}.flight_attach`, 'Flight attach'],
      [`cs.${team}.insurance_attach`, 'Insurance attach'],
      [`cs.${team}.luxplus_attach`, 'LuxPlus+ attach'],
    ])
    .concat([
      ['cs.sales.service_level', 'Call service level'],
      ['cs.sales.abandonment', 'Abandonment'],
      ['cs.sales.calls_answered', 'Calls answered'],
      ['cs.support.contacts', 'Contacts'],
      ['cs.support.service_level', 'Call service level'],
      ['cs.support.abandonment', 'Abandonment'],
      ['cs.support.email_same_day', 'Email same-day reply'],
      ['cs.support.calls_answered', 'Calls answered'],
      ['cs.support.aht', 'Average handle time'],
      ['cs.support.emails', 'Emails received'],
      ['cs.support.chats', 'Chats received'],
      ['cs.support.tickets_created', 'Tickets created'],
      ['cs.support.tickets_solved', 'Tickets solved'],
      ['cs.support.first_resolution_24h', 'Solved within 24h'],
      ['cs.support.contacts_per_100_orders', 'Contacts / 100 orders'],
    ]),
);

/** Rows that collect what has no name (no product, reason or mapping, off the roster, Other) sort under the named ones. */
const CATCH_ALL_ROWS = new Set(['Not attributed', 'Not categorised', 'Not mapped', 'Not on roster', 'Other']);

/** Said on a tile whose source has no history for the comparison (attachment starts on 1 Jan 2026). */
export const NO_COMPARISON_NOTE = 'No comparison yet';

/** One metric as a strip tile, with its sparkline and change line. */
export function csTile(
  metric: CustomerServiceMetric | undefined,
  id: string,
  label: string,
  snapshot: CustomerServiceSnapshot | undefined,
): AreaStripTile {
  const [reading] = readAreaMetrics(
    { metrics: { kind: 'governed', drillBreakdown: null, labels: { [id]: label }, metricIds: [id] } },
    metric ? [metric] : [],
  );
  if (reading.state !== 'ready') {
    return { id, label, note: snapshot ? 'Figures unavailable' : undefined, value: null };
  }
  const values = snapshot?.trend.series[id];
  const sparkline = values?.some((value) => value != null) ? { smoothWindow: 1, values } : null;
  if (snapshot?.comparison && metric?.comparisonValue == null) {
    return { changeText: NO_COMPARISON_NOTE, comparisonLabel: null, id, label, sparkline, value: reading.value };
  }
  return {
    changeText: reading.changeText,
    favourable: reading.tone === 'good',
    id,
    label,
    sparkline,
    value: reading.value,
  };
}

/**
 * Tiles that carry a second figure: Tickets solved reads with Tickets created beside it, so the card shows whether the
 * team kept up with what came in. The tile keeps the first metric's id, so Explore and Home read it as that metric.
 */
const CS_PAIRED: Readonly<Record<string, { pairedId: string; label: string }>> = {
  'cs.support.tickets_solved': { pairedId: 'cs.support.tickets_created', label: 'Tickets solved / created' },
};

/** The two metrics a paired tile carries, for Explore to switch between; null for a single-metric tile. */
export function csPairOf(metricId: string): ReadonlyArray<{ id: string; label: string }> | null {
  const paired = CS_PAIRED[metricId];
  if (!paired) {
    return null;
  }
  const [first, second] = paired.label.split(' / ');
  return [
    { id: metricId, label: first.replace(/^Tickets /, '').replace(/^\w/, (letter) => letter.toUpperCase()) },
    { id: paired.pairedId, label: second.replace(/^\w/, (letter) => letter.toUpperCase()) },
  ];
}

/** Two metrics on one tile: "20,194 / 20,317", each change beside the other, coloured by neither. */
function csPairedTile(first: AreaStripTile, second: AreaStripTile, label: string): AreaStripTile {
  if (first.value == null || second.value == null) {
    return { ...first, label };
  }
  const bothChange = first.changeText && second.changeText && first.changeText !== NO_COMPARISON_NOTE;
  return {
    changeText: bothChange ? `${first.changeText} / ${second.changeText}` : first.changeText,
    ...(first.comparisonLabel !== undefined ? { comparisonLabel: first.comparisonLabel } : {}),
    id: first.id,
    label,
    sparkline: first.sparkline,
    value: `${first.value} / ${second.value}`,
  };
}

/** The sales figures the Exclude cancellations view re-reads; conversion and attachment come from other sources. */
const CANCELLATION_METRIC = /^cs\.(sales|retail)\.(ttv|orders|abv|margin|margin_pct|aov)$/;

/**
 * Whether a figure leaves out cancellations. It follows the snapshot on screen rather than the button, so figures still
 * loading keep the flag they were read with.
 */
export function csExcludesCancellations(snapshot: CustomerServiceSnapshot | undefined, id: string): boolean {
  return (
    Boolean(snapshot?.caveats.some((caveat) => caveat.id === 'cancellations-excluded')) && CANCELLATION_METRIC.test(id)
  );
}

const tileLabel = (snapshot: CustomerServiceSnapshot | undefined, id: string) =>
  CS_LABELS[id] ?? snapshot?.metrics.find((metric) => metric.id === id)?.label ?? id;

/** Every metric's label as Explore's sentences read it, flagged in words when it leaves out cancellations. */
export function csMetricLabel(snapshot: CustomerServiceSnapshot | undefined, id: string): string {
  const label = tileLabel(snapshot, id);
  return csExcludesCancellations(snapshot, id) ? `${label} excl. cancellations` : label;
}

/** The three cards: headline tiles, then More metrics in sections captioned with the period and comparison. */
export function customerServiceSplit(snapshot: CustomerServiceSnapshot | undefined): Array<SplitStripGroup> {
  const byId = new Map((snapshot?.metrics ?? []).map((metric) => [metric.id, metric]));
  const caption = snapshot
    ? `${snapshot.period.label}${snapshot.comparison ? ' vs last year' : ''}`
    : 'Selected period';
  const single = (id: string) => {
    const read = csTile(byId.get(id), id, tileLabel(snapshot, id), snapshot);
    return csExcludesCancellations(snapshot, id) ? { ...read, badge: 'Excl. cancellations' } : read;
  };
  const tile = (id: string) => {
    const paired = CS_PAIRED[id];
    return paired ? csPairedTile(single(id), single(paired.pairedId), paired.label) : single(id);
  };
  return CS_TEAMS.map((team) => {
    const sections = CS_SECTIONS[team.id].map((section) => {
      const tiles = section.metricIds.map(tile);
      const badge = tiles.every((item) => item.badge) ? tiles[0]?.badge : undefined;
      return { caption, id: section.id, tiles, title: section.title, ...(badge ? { badge } : {}) };
    });
    return {
      headline: CS_HEADLINE[team.id].map(tile),
      label: team.label,
      more: sections.flatMap((section) => section.tiles),
      previewLabels: [],
      programme: team.id,
      sections,
    };
  });
}

/** Home's four tiles. */
export function customerServiceHomeTiles(snapshot: CustomerServiceSnapshot | undefined): Array<AreaStripTile> {
  const byId = new Map((snapshot?.metrics ?? []).map((metric) => [metric.id, metric]));
  return CS_HOME_TILES.map(({ id, label }) => csTile(byId.get(id), id, label, snapshot));
}

/** The team a metric belongs to, read from its id ('cs.<team>.<name>'). */
export function csTeamOf(metricId: string): CustomerServiceTeam {
  const team = metricId.split('.')[1];
  return team === 'retail' || team === 'support' ? team : 'sales';
}

/** The split Explore opens on for a metric: the team's first split that carries it, else the team's first split. */
export function csDefaultSplit<T extends { metricIds: Array<string> }>(
  splits: ReadonlyArray<T>,
  metricId: string,
): T | undefined {
  return splits.find((split) => split.metricIds.includes(metricId)) ?? splits[0];
}

/**
 * The splits Explore offers for a metric: only those that carry it, so every split reads the tile that was clicked. A
 * metric no split carries (email same-day reply) gets none, and Explore says it has no breakdown.
 */
export function csOfferedSplits<T extends { metricIds: Array<string> }>(
  splits: ReadonlyArray<T>,
  metricId: string,
): Array<T> {
  return splits.filter((split) => split.metricIds.includes(metricId));
}

export type CsSplitRow = {
  label: string;
  value: number | null;
  comparisonValue: number | null;
  percentChange: number | null;
};

/**
 * A split's rows for one metric, largest first. A split that does not carry the metric is read on its first metric, so
 * Explore always shows something for the team; `metricId` says which one was read.
 */
export function csSplitRows(
  split: CustomerServiceBreakdown | undefined,
  metricId: string,
): { metricId: string | null; rows: Array<CsSplitRow> } {
  if (!split) {
    return { metricId: null, rows: [] };
  }
  const shown = split.metricIds.includes(metricId) ? metricId : split.metricIds[0];
  const rows = split.rows
    .map((row) => {
      const found = row.metrics.find((item) => item.id === shown);
      return {
        comparisonValue: found?.comparisonValue ?? null,
        label: row.label,
        percentChange: found?.percentChange ?? null,
        value: found?.value ?? null,
      };
    })
    .filter((row) => row.value != null)
    .sort(
      (left, right) =>
        Number(CATCH_ALL_ROWS.has(left.label)) - Number(CATCH_ALL_ROWS.has(right.label)) ||
        (right.value ?? 0) - (left.value ?? 0) ||
        left.label.localeCompare(right.label),
    );
  return { metricId: shown, rows };
}
